import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef } from "react";

import { useConsoleLog } from "../../hooks/consoleLog";
// import { useCleanPath } from "../../hooks/useCleanPath";

import { useConfigLoader } from "../ConfigLoaderContext";
import { useEnvContext } from "../EnvironmentContext";
import { useGrowthbook } from "../GrowthBookContext";
import { useUrlContext } from "../urlContext";
import { useDevMode } from "../DevMode";

import { appActionCreators } from './appActions';
import { initialState } from './appState';
import { appConfigReducer } from './appReducer';
import { getRegion } from "../../utils/getRegion";
import { getMosCfg } from "../../utils/urlParams";

const AppConfigContext = createContext();
export const useAppConfig = () => useContext(AppConfigContext);

export const AppConfigProvider = ({ children }) => {
    const [state, dispatch] = useReducer(appConfigReducer, initialState);
    const logger = useConsoleLog();

    const { loadConfig } = useConfigLoader();
    const { isProduction } = useEnvContext();
    const { abValue } = useGrowthbook();

    const { devModeVariant } = useDevMode();
    const {
        defaultPagePath,
        newTpath,
        newT2path,
        newHpath,
        newH2path,
        newSpath,
        iqTestPath,
        applyDiscountPage,
        validRegularPathsCheck,
        holidayPromoPath,
        holidayPromo3Path,
        holidayPromo23dPath,
        validHolidayPathsCheck,
        learningDeskOnePath,
        w2aReadingPath,
        w2aLessonsPath,
        w2aMindPalacePath,
        w2aPath
    } = useUrlContext();

    const hasInitialized = useRef(false);
    const lastAbVariant = useRef(undefined);

    const pathEnvPrefix = isProduction ? "production" : "staging";

    const getConfig = useCallback(
        async (configPath) => {
            try {
                const config = await loadConfig(configPath);
                return config;
            } catch (error) {
                logger.error("Failed to fetch config:", error);
                return null;
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [loadConfig]
    );

    const getPagePrefixFromPath = useCallback(() => {
        const pathMap = {
            defaultPage: defaultPagePath && "new_h2",
            tPath: newTpath && "new_t",
            t2Path: newT2path && "new_t2",
            hPath: newHpath && "new_h",
            h2Path: newH2path && "new_h2",
            sPath: newSpath && "new_s",
            iqPath: iqTestPath && "iq_test",
            applyDiscount: applyDiscountPage && "apply_discount",
            hpPath: holidayPromoPath && "hp",
            hp3path: holidayPromo3Path && "hp3",
            hp23dpath: holidayPromo23dPath && "hp23d",
            w2aReadingPath: w2aReadingPath && "w2a_reading",
            w2aLessonsPath: w2aLessonsPath && "w2a_lessons",
            w2aMindPalacePath: w2aMindPalacePath && "w2a_mind_palace"
        };

        const result = Object.values(pathMap).find(value => value);
        return result || "new_h2";
    }, [
        defaultPagePath,
        newTpath,
        newT2path,
        newHpath,
        newH2path,
        newSpath,
        iqTestPath,
        applyDiscountPage,
        holidayPromoPath,
        holidayPromo3Path,
        holidayPromo23dPath,
        w2aReadingPath,
        w2aLessonsPath,
        w2aMindPalacePath
    ]);

    const setRegularConfig = useCallback(async (configData, abTestVariant) => {
        try {
            const pathPrefix = getPagePrefixFromPath();
            dispatch(appActionCreators.setPathPrefix(pathPrefix));

            const upsellsEnabled = configData.upsell_enabled[pathPrefix];
            dispatch(appActionCreators.setEnableUpsells(upsellsEnabled));

            const useABtest = configData.use_plan_page_ab_test[pathEnvPrefix][pathPrefix];
            const useQuizABtest = configData.use_quiz_ab_test[pathEnvPrefix][pathPrefix];
            const useUpsellABtest = configData.use_upsell_ab_test[pathEnvPrefix][pathPrefix];

            dispatch(appActionCreators.setFlowUseAbTest({ useABtest, useQuizABtest, useUpsellABtest }));

            logger.debug(`Plan page AB test on ${pathPrefix} flow:`, useABtest ? "enabled" : "disabled");
            if (!applyDiscountPage) {
                logger.debug(`Quiz AB test on ${pathPrefix} flow:`, useQuizABtest ? "enabled" : "disabled");
            }
            logger.debug(`Upsell AB test on ${pathPrefix} flow:`, useUpsellABtest ? "enabled" : "disabled");

            if (validHolidayPathsCheck) {
                if (useABtest) {
                    const pageContent = await getConfig(`${pathEnvPrefix}/abTest/holidayPromoFallback/${pathEnvPrefix}_${pathPrefix}_variant_${isProduction ? abTestVariant : devModeVariant}`);
                    dispatch(appActionCreators.setPageContent(pageContent));
                } else if (!useABtest) {
                    const pageContent = await getConfig(`${pathEnvPrefix}/default/holidayPromoFallback/${pathEnvPrefix}_${pathPrefix}_fallback`);
                    dispatch(appActionCreators.setPageContent(pageContent));
                }
            } else {
                if (useABtest) {
                    const pageContent = await getConfig(`${pathEnvPrefix}/abTest/regular/${pathEnvPrefix}_${pathPrefix}_variant_${isProduction ? abTestVariant : devModeVariant}`);
                    dispatch(appActionCreators.setPageContent(pageContent));
                } else if (!useABtest) {
                    const pageContent = await getConfig(`${pathEnvPrefix}/default/regular/${pathEnvPrefix}_${pathPrefix}_fallback`);
                    dispatch(appActionCreators.setPageContent(pageContent));
                }
            }


            if (!applyDiscountPage) {
                if (useQuizABtest) {
                    const quizData = await getConfig(`${pathEnvPrefix}/abTest/quiz/${pathEnvPrefix}_${pathPrefix}_quiz_variant_${isProduction ? abTestVariant : devModeVariant}`);
                    dispatch(appActionCreators.setQuizData(quizData));
                } else if (!useQuizABtest) {
                    const quizData = await getConfig(`${pathEnvPrefix}/quiz/${pathEnvPrefix}_${pathPrefix}_quiz_default`);
                    dispatch(appActionCreators.setQuizData(quizData));
                }
            }

            if (upsellsEnabled) {
                if (useUpsellABtest) {
                    const upsellsData = await getConfig(`${pathEnvPrefix}/abTest/upsell/${pathEnvPrefix}_upsell_${pathPrefix}_variant_${isProduction ? abTestVariant : devModeVariant}`);
                    dispatch(appActionCreators.setUpsellsData(upsellsData));
                } else if (!useUpsellABtest) {
                    const upsellsData = await getConfig(`${pathEnvPrefix}/default/upsell/${pathEnvPrefix}_upsell_${pathPrefix}_fallback`);
                    dispatch(appActionCreators.setUpsellsData(upsellsData));
                }
            }
        } catch (error) {
            logger.critical("Local regular path config error, ", error);
        } finally {
            dispatch(appActionCreators.setIsConfigReady(true));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getPagePrefixFromPath, getConfig, pathEnvPrefix, isProduction, devModeVariant, applyDiscountPage]);

    const setRegularPageContent = useCallback(async () => {
        try {
            const componentsConfigPath = `${pathEnvPrefix}/plan/${pathEnvPrefix}_default_components_plan_page`;

            const componentsHolidayPromoConfigPath = `${pathEnvPrefix}/plan/${pathEnvPrefix}_holiday_promo_plan_page`;

            // const componentsHolidayPromoConfigPath = `components_plan_page`;

            const [componentsData, newPageReviews, videoReviews, feedbackData, optionTextData] = await Promise.all([
                getConfig(validHolidayPathsCheck ? componentsHolidayPromoConfigPath : componentsConfigPath),
                getConfig("reviews"),
                getConfig("video_reviews"),
                getConfig("feedback"),
                getConfig(`${pathEnvPrefix}/${pathEnvPrefix}_option_text_config`)
            ]);

            dispatch(appActionCreators.setMultipleConfig({
                componentsData,
                newPageReviews,
                newPageVideoReviews: videoReviews,
                feedbackData,
                optionTextData
            }));
        } catch (error) {
            logger.error("Error loading regular page content:", error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getConfig, pathEnvPrefix, validHolidayPathsCheck]);

    const getFlowKey = useCallback(() => {
        if (defaultPagePath) return "new_h_2_path";
        if (newTpath) return "new_t_path";
        if (newT2path) return "new_t2_path";
        if (newHpath) return "new_h_path";
        if (newH2path) return "new_h_2_path";
        if (iqTestPath) return "iq_test_path";
        if (applyDiscountPage) return "apply_discount_path";
        if (holidayPromoPath) return "hp";
        if (holidayPromo3Path) return "hp3";
        if (holidayPromo23dPath) return "hp23d";
        return "new_h_2_path";
    }, [
        defaultPagePath,
        newTpath,
        newT2path,
        newHpath,
        newH2path,
        iqTestPath,
        applyDiscountPage,
        holidayPromoPath,
        holidayPromo3Path,
        holidayPromo23dPath
    ]);

    const setData = useCallback(async (abTestVariant) => {
        if (lastAbVariant.current === abTestVariant && hasInitialized.current) {
            return;
        }

        lastAbVariant.current = abTestVariant;
        hasInitialized.current = true;

        if (!abTestVariant) {
            logger.warn("Fallback variant: enabled");
            dispatch(appActionCreators.setHideDevMode(true));
        }

        try {
            const configData = await getConfig("app_config");
            dispatch(appActionCreators.setDisclaimerText(configData.disclaimer_re_enter_payment));
            dispatch(appActionCreators.setAnalyticsData(configData.default_analytics_data));
            const currentFlowKey = getFlowKey();

            const abTestCampaignName = configData.appConfig.REACT_APP_A_B_TEST_CAMPAIGN_NAME[currentFlowKey];
            const abTestStartCampaignDate = configData.appConfig.REACT_APP_START_TEST_CAMPAIGN_DATE[currentFlowKey];

            dispatch(appActionCreators.setMultipleConfig({
                appData: configData.appConfig,
                paymentButtons: configData.payment_buttons,
                redirectUrl: configData.reservePageRedirectURL,
                emailProviders: configData.emailServices,
                QAtimerTime: configData.qa_timer_time,
                emailsToDisableAnalyticsEvent: configData.teamEmailsToDisableAnalyticsEvents,
                apiGeoUrl: configData.appConfig.REACT_APP_GEO_API,
                abTestCampaignName: abTestCampaignName,
                abTestStartCampaignDate: abTestStartCampaignDate
            }));

            if (validRegularPathsCheck) {
                await setRegularConfig(configData, abTestVariant);
            } else {
                await setRegularConfig(configData, abTestVariant);
            }

        } catch (error) {
            logger.critical(`Local config setData error, `, error);
        } finally {
            dispatch(appActionCreators.setIsConfigReady(true));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getConfig, validRegularPathsCheck, setRegularConfig, getFlowKey]);

    // Learning Desk One is a static advertorial: only the base app config is needed, no flow/A-B/page content configs.
    const setLearningDeskConfig = async () => {
        try {
            const configData = await getConfig("app_config");
            dispatch(appActionCreators.setAnalyticsData(configData.default_analytics_data));
            dispatch(appActionCreators.setMultipleConfig({
                appData: configData.appConfig,
                emailsToDisableAnalyticsEvent: configData.teamEmailsToDisableAnalyticsEvents,
                apiGeoUrl: configData.appConfig.REACT_APP_GEO_API
            }));
        } catch (error) {
            logger.critical("Learning Desk One config error, ", error);
        } finally {
            dispatch(appActionCreators.setIsConfigReady(true));
        }
    };

    const getFlowConfig = async (abTestVariant, mosUtmConfig) => {
        try {
            const pathPrefix = getPagePrefixFromPath();
            dispatch(appActionCreators.setPathPrefix(pathPrefix));

            const configData = await getConfig("app_config");
            dispatch(appActionCreators.setDisclaimerText(configData.disclaimer_re_enter_payment));
            dispatch(appActionCreators.setDisclaimerOneTimeText(configData.disclaimer_one_time_payment));

            const upsellsEnabled = configData.upsell_enabled[pathPrefix];
            dispatch(appActionCreators.setEnableUpsells(upsellsEnabled));

            dispatch(appActionCreators.setMultipleConfig({
                appData: configData.appConfig,
                paymentButtons: configData.payment_buttons,
                redirectUrl: configData.reservePageRedirectURL,
                emailProviders: configData.emailServices,
                QAtimerTime: configData.qa_timer_time,
                emailsToDisableAnalyticsEvent: configData.teamEmailsToDisableAnalyticsEvents,
                apiGeoUrl: configData.appConfig.REACT_APP_GEO_API,
            }));

            const testGeoApi = configData.appConfig.REACT_APP_GEO_API;
            // TODO-HP: Restore regional selection when HP EUR/GBP configs are added.
            // The backend HP API rollout does not replace these local currency configs.
            const isSingleConfigHolidayFlow = holidayPromoPath || holidayPromo3Path || holidayPromo23dPath;
            // W2A pages are static and do not depend on the geo service or regional configs.
            const skipGeo = isSingleConfigHolidayFlow || w2aPath;
            const geoData = skipGeo ? {} : await (await fetch(testGeoApi)).json();
            const countryCode = geoData.country?.toUpperCase();
            dispatch(appActionCreators.setMultipleConfig({ countryCode: countryCode || "" }));
            const configRegionCode = skipGeo ? null : getRegion(countryCode);
            logger.debug(`geoData`, geoData);
            logger.debug(`configRegionCode`, configRegionCode);

            const use_ab_test = configData.use_ab_test[pathEnvPrefix][pathPrefix];

            // TODO: Remove NewH US-only AB eligibility after the experiment is finished.
            // const isUSCountry = countryCode === "US";
            // const shouldUseAbTest = Boolean(use_ab_test && isUSCountry);
            // const defaultConfigRegionCode = isUSCountry ? null : configRegionCode;

            const shouldUseAbTest = Boolean(use_ab_test);
            const defaultConfigRegionCode = configRegionCode;
            dispatch(appActionCreators.setFlowUseAbTest({ use_ab_test: shouldUseAbTest }));

            let flowConfigPath;
            let flowConfig;

            if (mosUtmConfig) {
                flowConfigPath = `${pathEnvPrefix}/${pathPrefix}/${pathPrefix}_${mosUtmConfig}`;
                logger.debug(`flowConfigPath [mos_cfg override]`, flowConfigPath);

                flowConfig = await getConfig(flowConfigPath);

                if (!flowConfig || Object.keys(flowConfig).length === 0) {
                    logger.warn(`Config not found for mos_cfg="${mosUtmConfig}", falling back to default`);
                    flowConfigPath = `${pathEnvPrefix}/${pathPrefix}/${pathPrefix}_default`;
                    flowConfig = await getConfig(flowConfigPath);
                }
            } else if (shouldUseAbTest) {
                const abValueConfig = isProduction ? abTestVariant : devModeVariant;
                const defaultAbConfigPath = `${pathEnvPrefix}/${pathPrefix}/ab_test/${pathPrefix}_variant_${abValueConfig}`;

                flowConfigPath = defaultConfigRegionCode
                    ? `${pathEnvPrefix}/${pathPrefix}/ab_test/${pathPrefix}_variant_${defaultConfigRegionCode}_${abValueConfig}`
                    : defaultAbConfigPath;
                logger.debug(`flowConfigPath [ab_test]`, flowConfigPath);
                flowConfig = await getConfig(flowConfigPath);

                if (defaultConfigRegionCode && (!flowConfig || Object.keys(flowConfig).length === 0)) {
                    logger.warn(`Regional AB config not found: ${flowConfigPath}, falling back to USD variant ${abValueConfig}`);
                    flowConfigPath = defaultAbConfigPath;
                    logger.debug(`flowConfigPath [ab_test]`, flowConfigPath);
                    flowConfig = await getConfig(flowConfigPath);
                }
            } else {
                if (defaultConfigRegionCode && !newT2path) {
                    flowConfigPath = `${pathEnvPrefix}/${pathPrefix}/${pathPrefix}_default_${defaultConfigRegionCode}`;
                    logger.debug(`flowConfigPath [default]`, flowConfigPath);
                    flowConfig = await getConfig(flowConfigPath);
                } else {
                    flowConfigPath = `${pathEnvPrefix}/${pathPrefix}/${pathPrefix}_default`;
                    logger.debug(`flowConfigPath [default]`, flowConfigPath);
                    flowConfig = await getConfig(flowConfigPath);
                }
            }

            dispatch(appActionCreators.setMultipleConfig({
                flowData: flowConfig,
                analyticsData: flowConfig.system.analytics,
                abTestCampaignName: flowConfig.system.analytics.ab_test_name,
                abTestStartCampaignDate: flowConfig.system.analytics.ab_test_start_date,
                shouldUseAbTestFlag: shouldUseAbTest
            }));

        }
        catch (error) {
            logger.critical(`Get Flow Config error, `, error);
        }
        finally {
            dispatch(appActionCreators.setIsConfigReady(true));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }

    const mosUtmConfig = getMosCfg();

    useEffect(() => {
        if (mosUtmConfig === null) {
            if (abValue || abValue === null) {
                dispatch(appActionCreators.setAbTestVariant(abValue));
            }
        } else {
            dispatch(appActionCreators.setAbTestVariant(null));
        }

    }, [abValue, mosUtmConfig]);

    const hasLoadedContentRef = useRef(false);

    useEffect(() => {
        if (learningDeskOnePath) {
            if (!hasLoadedContentRef.current) {
                hasLoadedContentRef.current = true;
                setLearningDeskConfig();
            }
            return;
        }

        if (!newHpath && !iqTestPath && !newH2path && !newT2path && !newSpath && !holidayPromoPath && !holidayPromo3Path && !holidayPromo23dPath && !w2aPath) {
            if (state.abTestVariant !== undefined) {
                setData(state.abTestVariant);
            }
            if (!hasLoadedContentRef.current) {
                setRegularPageContent();
                hasLoadedContentRef.current = true;
            }
            return;
        }

        if (state.abTestVariant === undefined) return;

        getFlowConfig(state.abTestVariant, mosUtmConfig);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newHpath, iqTestPath, newH2path, newT2path, newSpath, learningDeskOnePath, holidayPromoPath, holidayPromo3Path, holidayPromo23dPath, w2aPath, state.abTestVariant, mosUtmConfig]);

    const contextValue = useMemo(() => ({
        apiGeoUrl: state.apiGeoUrl,
        countryCode: state.countryCode,
        abTestCampaignName: state.abTestCampaignName,
        abTestStartCampaignDate: state.abTestStartCampaignDate,
        paymentButtons: state.paymentButtons,
        quizData: state.quizData,
        appData: state.appData,
        feedbackData: state.feedbackData,
        componentsData: state.componentsData,
        newPageReviews: state.newPageReviews,
        newPageVideoReviews: state.newPageVideoReviews,
        redirectUrl: state.redirectUrl,
        emailProviders: state.emailProviders,
        emailsToDisableAnalyticsEvent: state.emailsToDisableAnalyticsEvent,
        remotePageContent: state.remotePageContent,
        upsellsPageData: state.upsellsPageData,
        enableUpsells: state.enableUpsells,
        optionTextData: state.optionTextData,
        hideDevMode: state.hideDevMode,
        abTestVariant: state.abTestVariant,
        QAtimerTime: state.QAtimerTime,
        isConfigReady: state.isConfigReady,
        flowUseABtest: state.flowUseABtest,
        pathPrefix: state.pathPrefix,
        setRegularPageContent,
        dispatch,
        actionCreators: appActionCreators,
        flowData: state.flowData,
        analyticsData: state.analyticsData,
        mosUtmConfig: mosUtmConfig,
        disclaimerText: state.disclaimerText,
        disclaimerOneTimeText: state.disclaimerOneTimeText,
        shouldUseAbTestFlag: state.shouldUseAbTestFlag
    }), [
        state.QAtimerTime,
        state.abTestCampaignName,
        state.abTestStartCampaignDate,
        state.abTestVariant,
        state.apiGeoUrl,
        state.countryCode,
        state.appData,
        state.componentsData,
        state.emailProviders,
        state.emailsToDisableAnalyticsEvent,
        state.feedbackData,
        state.hideDevMode,
        state.isConfigReady,
        state.newPageReviews,
        state.newPageVideoReviews,
        state.optionTextData,
        state.paymentButtons,
        state.quizData,
        state.redirectUrl,
        state.remotePageContent,
        state.upsellsPageData,
        state.enableUpsells,
        state.flowUseABtest,
        state.pathPrefix,
        setRegularPageContent,
        dispatch,
        state.flowData,
        state.analyticsData,
        mosUtmConfig,
        state.disclaimerText,
        state.disclaimerOneTimeText,
        state.shouldUseAbTestFlag
    ]);

    if (!state.isConfigReady && !newHpath && !iqTestPath && !newT2path && !newH2path && !newSpath) {
        return false;
    }

    return (
        <AppConfigContext.Provider value={contextValue}>
            {children}
        </AppConfigContext.Provider>
    );
};
